import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Calendar, DayRange, DayValue } from '@hassanmojab/react-modern-calendar-datepicker';
import '@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Toolbar,
  Container,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Typography,
  Box,
  IconButton,
  Popover,
  Autocomplete,
  Chip,
  Tooltip,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { format } from 'date-fns';
import axios from 'axios';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import CollisionRiskTable from './CollisionRiskTable';
import ChartsContainer from './Charts/ChartsContainer';
import { Customer, DataPoint } from '../types';
import { AppContext } from '../../AppContext';
import MenuItem from '@mui/material/MenuItem';

const MAX_SELECTED_CUSTOMERS = 3;

const generateHanoverFleetsData = (customers: Customer[]): DataPoint[] => {
  if (customers.length === 0) return [];

  const dateSet = new Set<number>();
  customers.forEach((customer) => {
    customer.data.forEach((d) => dateSet.add(d.date));
  });
  const dates = Array.from(dateSet).sort((a, b) => a - b);

  let lastBenchmarkRisk: number | null = null;
  const hanoverFleetsData: DataPoint[] = [];

  dates.forEach((date) => {
    let totalDevices = 0;
    let totalWeightedCollisionRisk = 0;

    customers.forEach((customer) => {
      const dataPoint = customer.data.find((d) => d.date === date);
      const numDevices = customer.numActiveDevices || 0;
      if (numDevices > 0 && dataPoint) {
        if (dataPoint.collisionRisk != null && dataPoint.collisionRisk !== 0) {
          totalDevices += numDevices;
          totalWeightedCollisionRisk += dataPoint.collisionRisk * numDevices;
        }
      }
    });

    let avgCollisionRisk: number | null = null;
    if (totalDevices > 0) {
      avgCollisionRisk = totalWeightedCollisionRisk / totalDevices;
    }

    let avgBenchmarkRisk: number | null = null;
    if (lastBenchmarkRisk != null) {
      avgBenchmarkRisk = lastBenchmarkRisk;
    }

    if (avgCollisionRisk != null || avgBenchmarkRisk != null) {
      hanoverFleetsData.push({
        date,
        collisionRisk: avgCollisionRisk != null ? +avgCollisionRisk.toFixed(1) : null,
      });
    }
  });

  return hanoverFleetsData;
};

const formatDate = (day: DayValue): string => {
  if (!day) return '';
  const date = new Date(day.year, day.month - 1, day.day);
  return format(date, 'MMM dd, yyyy');
};

const CollisionRiskDashboard: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { user: appUser } = useContext(AppContext);

  const [regionCustomers, setRegionCustomers] = useState<Customer[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [hanoverFleetsData, setHanoverFleetsData] = useState<DataPoint[]>([]);
  const [dateRangeOption, setDateRangeOption] = useState<'30days' | '6months' | 'year' | 'all' | 'custom'>('all');
  const [customDateRange, setCustomDateRange] = useState<DayRange>({ from: null, to: null });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedFleetId, setSelectedFleetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [showInactive, setShowInactive] = useState(false);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const handleFleetSelect = (fleetId: string) => {
    setSelectedFleetId(fleetId);
  };

  const convertToDate = (day: DayValue, isEndDate = false): Date | null => {
    if (!day) return null;
    const date = new Date(day.year, day.month - 1, day.day);
    if (isEndDate) {
      date.setHours(23, 59, 59, 999);
    }
    return date;
  };

  useEffect(() => {
    const fetchAllCustomers = async () => {
      try {
        const resp = await axios.get(`${process.env.REACT_APP_API_URL}/customers`);
        let data: Customer[] = resp.data.map((c: any) => ({
          ...c,
          id: c.id.toString(),
        }));
        if (appUser?.region_id && appUser.region_id.toString() !== '1') {
          data = data.filter((c) => c.region_id.toString() === appUser.region_id?.toString());
        }
        const sortedData = data.sort((a, b) =>
          a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1
        );
        setRegionCustomers(sortedData);
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };
    fetchAllCustomers();
  }, [appUser?.region_id]);

  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!startDate || !endDate) return

    const controller = new AbortController()
    const currentRequestId = ++requestIdRef.current
    setLoading(true)

    // If (endDate – startDate) > 6 months, split into multiple ranges.
    function buildYearlyRanges(start: Date, end: Date) {
      const ranges: Array<{ start: Date; end: Date }> = []
      let currentDate = new Date(start)

      while (currentDate <= end) {
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()

        // If currentDate is in Jan–Jun (0–5), chunkEnd = June 30 of that year.
        // Otherwise (Jul–Dec), chunkEnd = Dec 31 of that year.
        let boundary: Date
        if (month <= 5) {
          boundary = new Date(year, 5, 30) // June 30
        } else {
          boundary = new Date(year, 11, 31) // Dec 31
        }

        // Clamp to the requested 'end' if necessary:
        const chunkEnd = boundary < end ? boundary : end

        ranges.push({
          start: new Date(currentDate),
          end: new Date(chunkEnd),
        })

        // Move currentDate to the day after chunkEnd:
        currentDate = new Date(chunkEnd)
        currentDate.setDate(currentDate.getDate() + 1)
      }

      return ranges
    }

    const fetchData = async () => {
      try {
        const sixMonthsLater = new Date(startDate)
        sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6)

        let fetchPromises: Array<Promise<any>>

        if (sixMonthsLater >= endDate) {
          // Entire range <= 6 months then just one request
          const params = {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0],
          }
          fetchPromises = [
            axios.get(`${process.env.REACT_APP_API_URL}/collision_risks`, {
              params,
              signal: controller.signal,
            }),
          ]
        } else {
          // Range > 6 months then split into 6-month (calendar-aligned) chunks
          const ranges = buildYearlyRanges(startDate, endDate)
          fetchPromises = ranges.map(({ start, end }) => {
            const params = {
              start_date: start.toISOString().split('T')[0],
              end_date: end.toISOString().split('T')[0],
            }
            return axios.get(`${process.env.REACT_APP_API_URL}/collision_risks`, {
              params,
              signal: controller.signal,
            })
          })
        }

        // Wait for all chunked requests (or just the one) to finish:
        const responses = await Promise.all(fetchPromises)

        const mergedById: Record<string, Customer> = {}

        for (const response of responses) {
          const partialCustomers: any[] = response.data

          partialCustomers.forEach((rawCustomer) => {
            const customerId = rawCustomer.id.toString()

              if (!mergedById[customerId]) {
                // First time seeing this customer - clone all fields
                mergedById[customerId] = {
                  ...rawCustomer,
                  id: customerId,
                  data: Array.isArray(rawCustomer.data) ? [...rawCustomer.data] : [],
                  engagementData: Array.isArray(rawCustomer.engagementData)
                    ? [...rawCustomer.engagementData]
                    : [],
                }
              } else {
                // Already have this customer - just concatenate its 'data[]'
                mergedById[customerId].data.push(...(rawCustomer.data ?? []))
                mergedById[customerId].engagementData = [
                  ...(mergedById[customerId].engagementData ?? []),
                  ...(rawCustomer.engagementData ?? []),
                ]
              }
            })
          }

          // Convert merged map back into an array, sort each 'data[]', set collisionRisk:
          const finalArray: Customer[] = Object.values(mergedById).map((customer) => {
            if (customer.data.length > 0) {
              customer.data.sort((a, b) => b.date - a.date)
              // The latest collisionRisk is the first element after sorting
              customer.collisionRisk = customer.data[0].collisionRisk
            }

            if (customer.engagementData && customer.engagementData.length > 0) {
              customer.engagementData = Array.from(
                new Map(customer.engagementData.map((point) => [point.date, point])).values()
              ).sort((a, b) => a.date - b.date)
            }

            return customer
          })

        // If still the latest request, update state:
        if (requestIdRef.current === currentRequestId) {
          setCustomers(finalArray)
          setLoading(false)
        }
      } catch (err) {
        if (axios.isCancel(err)) {
        } else {
          console.error('Error fetching data:', err)
          if (requestIdRef.current === currentRequestId) {
            setLoading(false)
          }
        }
      }
    }

    fetchData()

    return () => {
      controller.abort()
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (customers.length === 0) return;
    const fleetsData = generateHanoverFleetsData(customers);
    setHanoverFleetsData(fleetsData);
  }, [customers]);

  useEffect(() => {
    const now = new Date();
    let newStartDate: Date;
    let newEndDate: Date;

    if (dateRangeOption === 'custom') {
      if (customDateRange.from && customDateRange.to) {
        const fromDate = convertToDate(customDateRange.from);
        const toDate = convertToDate(customDateRange.to, true);
        if (fromDate && toDate) {
          setStartDate(fromDate);
          setEndDate(toDate);
        }
      }
      return;
    }

    switch (dateRangeOption) {
      case '30days':
        newStartDate = new Date(now.getTime() - 29 * 86400000);
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate = new Date();
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case '6months':
        newStartDate = new Date(now);
        newStartDate.setMonth(now.getMonth() - 5);
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate = new Date();
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case 'year':
        newStartDate = new Date(now);
        newStartDate.setFullYear(now.getFullYear() - 1);
        newStartDate.setDate(newStartDate.getDate() + 1);
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate = new Date();
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case 'all':
        newStartDate = new Date('2020-01-01');
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate = now;
        newEndDate.setHours(23, 59, 59, 999);
        break;
      default:
        newStartDate = new Date('2020-01-01');
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate = now;
        newEndDate.setHours(23, 59, 59, 999);
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, [dateRangeOption, customDateRange]);

  const handleCheckboxChange = (customerId: string) => {
    const match = regionCustomers.find((c) => c.id === customerId);
    if (!match) return;

    setSelectedCustomers((prev) => {
      const already = prev.some((c) => c.id === match.id);
      if (already) {
        return prev.filter((c) => c.id !== match.id);
      } else if (prev.length < MAX_SELECTED_CUSTOMERS) {
        return [...prev, match];
      }
      return prev;
    });
  };

  const availableCustomerIds = useMemo(() => {
    return customers
      .filter((c) => c.collisionRisk !== null)
      .map((c) => c.id);
  }, [customers]);

  useEffect(() => {
    setSelectedCustomers((prevSelected) =>
      prevSelected.filter((c) => availableCustomerIds.includes(c.id))
    );
  }, [availableCustomerIds]);

  const filteredRegionCustomers = regionCustomers.filter(
    (c) => c.active === (showInactive ? false : true)
  );
  return (
    <>
      <Toolbar />
      <Container maxWidth="lg">
        <Box sx={{ marginTop: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label={showInactive ? "Inactive Accounts" : "Active Accounts"}
          />
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={filteredRegionCustomers}
            value={selectedCustomers}
            onChange={(event, newValue) => {
              if (newValue.length <= MAX_SELECTED_CUSTOMERS) {
                setSelectedCustomers(newValue);
              }
            }}
            getOptionLabel={(option) =>
              option.account_number
                ? `${option.name} ${option.account_number}`
                : option.name
            }
            renderTags={() => null}
            renderOption={(props, option, { selected }) => {
              const { key, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
              const hasData = availableCustomerIds.includes(option.id);
              const isMaxSelected = !selected && selectedCustomers.length >= MAX_SELECTED_CUSTOMERS;
              const isDisabled = isMaxSelected || !hasData;

              let tooltipTitle = "";
              if (!hasData) {
                tooltipTitle = "No data available for this customer";
              } else if (isMaxSelected) {
                tooltipTitle = `Max ${MAX_SELECTED_CUSTOMERS} customers selected`;
              }

              const displayLabel = option.account_number
                ? `${option.name} (${option.account_number})`
                : option.name;

              return (
                <li
                  {...liProps}
                  key={option.id}
                  style={{
                    ...(liProps.style || {}),
                    pointerEvents: isDisabled ? 'none' : 'auto',
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                >
                  {tooltipTitle ? (
                    <Tooltip title={tooltipTitle} placement="right">
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          disabled={isDisabled}
                          checked={selected}
                        />
                        {displayLabel}
                      </span>
                    </Tooltip>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        disabled={isDisabled}
                        checked={selected}
                      />
                      {displayLabel}
                    </span>
                  )}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search customers"
                placeholder="Type name or account number..."
              />
            )}
            sx={{ width: 300 }}
          />

            <FormControl variant="outlined" sx={{ minWidth: 200, marginRight: 2 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRangeOption}
                onChange={(e) =>
                  setDateRangeOption(e.target.value as '30days' | '6months' | 'year' | 'all' | 'custom')
                }
                label="Date Range"
              >
                <MenuItem value="30days">Previous 30 Days</MenuItem>
                <MenuItem value="6months">Previous 6 Months</MenuItem>
                <MenuItem value="year">Previous Year</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>

            {dateRangeOption === 'custom' && (
              <Box sx={{ marginLeft: 2 }}>
                <IconButton
                  aria-label="Select date range"
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                  <CalendarTodayIcon />
                  {customDateRange.from && customDateRange.to && (
                    <Typography sx={{ marginLeft: 1 }}>
                      {formatDate(customDateRange.from)} - {formatDate(customDateRange.to)}
                    </Typography>
                  )}
                </IconButton>
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2 }}>
                    <Calendar
                      value={customDateRange}
                      onChange={(range) => {
                        setCustomDateRange(range);
                        if (range.from && range.to) {
                          setAnchorEl(null);
                        }
                      }}
                      shouldHighlightWeekends
                      colorPrimary="#0fbcf9"
                      colorPrimaryLight="#d1ecf1"
                    />
                  </Box>
                </Popover>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
            {selectedCustomers.map((customer) => (
            <Chip
              key={customer.id}
              label={
                customer.account_number
                  ? `${customer.name} (${customer.account_number})`
                  : customer.name
              }
              onDelete={() =>
                setSelectedCustomers((prev) => prev.filter((c) => c.id !== customer.id))
              }
            />
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <ChartsContainer
            loading={loading}
            data={hanoverFleetsData}
            customers={customers}
            regionCustomers={regionCustomers}
            startDate={startDate}
            endDate={endDate}
            selectedCustomerIds={selectedCustomers.map((c) => c.id)}
          />
        </Box>

        <CollisionRiskTable
          selectedFleetId={selectedFleetId}
          onFleetSelect={handleFleetSelect}
          selectedCustomerIds={selectedCustomers.map((c) => c.id)}
          maxSelectedCustomers={MAX_SELECTED_CUSTOMERS}
          selectedCustomerCount={selectedCustomers.length}
          onCheckboxChange={handleCheckboxChange}
          availableCustomerIds={availableCustomerIds}
          showInactive={showInactive}
          startDate={startDate}
          endDate={endDate}
        />
      </Container>
    </>
  );
};

export default CollisionRiskDashboard;
